import { fetchPatientReturnConfig } from '@/entities/patient-return';
import {
  fetchRmaLimitConfig,
  fetchRmaOptionLists,
  fetchRmaTicketCount,
  useRmaStore,
} from '@/entities/rma';
import { fetchMe, useUserStore, handleCheckRoles } from '@/entities/user';
import { EMITTER_CONSTANTS, LOADING_TYPES } from '@/shared/config';
import { getUserAvatar } from '@/shared/api';
import { auth, emitter, GETTEXT_ALL_FACILITIES, gettextSeeAllPatients } from '@/shared/lib';
import { usePhysicianStore } from '@/entities/physician';
import { useSupportStore } from '@/entities/support';
import { useLoadingStore } from '@/shared/model';
import _ from 'lodash';
import {
  facilitySelection,
  removeClosedFacilities,
  resolveSelectedFacility,
} from '@/entities/facility';

/**
 * Bootstraps the signed-in session: profile, facilities, physicians, RMA options and limits and patient preferences. Fetches me and emits ACCESS_REMOVED on an invalid role set, resolves the Biocare avatar, loads the RMA option lists and limit config (left empty when a query fails), patient return config and the shipment-reminder ticket count, prunes closed facilities and settles the persisted facility and see-patients selections, queueing every store write into applyEffects flushed in one pass.
 *
 * @returns Resolves once every queued store update has been applied.
 */
const initSession = async () => {
  useLoadingStore.getState().setLoadingText(LOADING_TYPES.LOADING);

  const applyEffects: Array<() => void> = [];

  const meResult = await fetchMe();
  if (meResult) {
    const { photo, supportFacilities } = meResult;

    const isValidRole = handleCheckRoles(meResult?.roles);
    if (!isValidRole) {
      emitter.emit(EMITTER_CONSTANTS.ACCESS_REMOVED);
    }

    auth.setUserData(meResult);

    if (auth.isLoginWithBiocarePortal() && photo) {
      const avatarLink = await getUserAvatar();
      meResult.avatarLink = avatarLink;
    }

    applyEffects.push(() => useUserStore.getState().setMe(meResult));

    const [rmaOptionLists, rmaLimitConfig, patientReturnConfig] = await Promise.all([
      fetchRmaOptionLists(),
      fetchRmaLimitConfig().catch((error) => {
        console.error('Failed to fetch RMA limit config: ', error);
        return null;
      }),
      fetchPatientReturnConfig(),
    ]);
    if (!_.isEmpty(rmaOptionLists)) {
      applyEffects.push(() => useRmaStore.getState().setRmaOptionLists(rmaOptionLists));
    }
    if (rmaLimitConfig) {
      applyEffects.push(() => useRmaStore.getState().setRmaLimitConfig(rmaLimitConfig));
    }
    if (!_.isEmpty(patientReturnConfig)) {
      applyEffects.push(() => useRmaStore.getState().setPatientReturnConfig(patientReturnConfig));
    }

    if (supportFacilities) {
      removeClosedFacilities(supportFacilities);
      facilitySelection.setUserSupportFacilities(supportFacilities);
      let selectedFacilityLocalStorage = facilitySelection.selectedFacilityLocalStorage();
      const nextSelectedFacility = resolveSelectedFacility(
        supportFacilities,
        selectedFacilityLocalStorage,
      );
      if (nextSelectedFacility) {
        selectedFacilityLocalStorage = nextSelectedFacility;
        facilitySelection.setSelectedFacilityLocalStorage(nextSelectedFacility);
        applyEffects.push(() =>
          useUserStore.getState().changeSelectedFacility(nextSelectedFacility),
        );
      }

      const rmaTicketCount = await fetchRmaTicketCount({
        clinicShipmentReminder: true,
        facilityId:
          selectedFacilityLocalStorage.value === GETTEXT_ALL_FACILITIES
            ? undefined
            : selectedFacilityLocalStorage.value,
      });

      applyEffects.push(() => {
        usePhysicianStore.getState().fetchPhysicians(selectedFacilityLocalStorage.value);
      });
      applyEffects.push(() => {
        useSupportStore.getState().setSupportFacilities(supportFacilities);
      });

      applyEffects.push(() => useRmaStore.getState().setRmaTicketCount(rmaTicketCount ?? 0));
    }
  }

  let selectedTypeSeePatientsLocalStorage = facilitySelection.selectedTypeSeePatientsLocalStorage();
  if (selectedTypeSeePatientsLocalStorage?.value) {
    const typeSeePatientsToApply = selectedTypeSeePatientsLocalStorage;
    applyEffects.push(() =>
      useUserStore.getState().changeSelectedTypeSeePatients(typeSeePatientsToApply),
    );
  } else {
    selectedTypeSeePatientsLocalStorage = {
      label: gettextSeeAllPatients('Your studies'),
      value: 'Your studies',
    };
    facilitySelection.setSelectedTypeSeePatientsLocalStorage(selectedTypeSeePatientsLocalStorage);
    const typeSeePatientsToApply = selectedTypeSeePatientsLocalStorage;
    applyEffects.push(() =>
      useUserStore.getState().changeSelectedTypeSeePatients(typeSeePatientsToApply),
    );
  }

  applyEffects.push(() => useLoadingStore.getState().setLoadingText(''));

  _.forEach(applyEffects, (fn) => fn());
};

export default initSession;
